import type { ReactNode } from 'react';
import { ArrowRight, Check, ChevronDown, ChevronLeft, Mic, MicOff, Play } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'components/dropdown/dropdown';
import { formatLanguageCode } from 'utils/audioUpload';
import type { Language } from 'types/language';
import {
  BackButton,
  FooterButtonRow,
  FooterContainer,
  LanguageMenuDescription,
  LanguageMenuHeader,
  LanguageMenuTitle,
  LanguageMenuTrigger,
  LanguageOption,
  LanguageOptionCode,
  LanguageOptionLabel,
  LanguageOptionMeta,
  NoRecordingOption,
  RecordingActionButton,
  RecordingActionGroup,
  RecordingActionLabel,
  RecordingActionSubtitle,
  RecordingActionTitle,
  ResumeRecordingButton,
} from './PatientOverviewFooter.styles';

export const NO_RECORDING_SELECTION = 'no-recording';

interface PatientOverviewFooterProps {
  selectedLanguageCode: string;
  languagesData: { languages: Language[]; defaultLanguageCode?: string } | undefined;
  lastUsedLanguageCode?: string | null;
  isResolvingLanguage?: boolean;
  hasLanguageError?: boolean;
  isRecordingRestricted?: boolean;
  isLoadingPatient?: boolean;
  isCheckingPausedConsultation?: boolean;
  resume?: { languageCode: string | null; onResume: () => void };
  onLanguageChange: (value: string) => void;
  onStartRecording: () => void;
  onStartWithoutRecording: () => void;
  onBackToOverview: () => void;
  children?: ReactNode;
}

export const PatientOverviewFooter = ({
  selectedLanguageCode,
  languagesData,
  lastUsedLanguageCode,
  isResolvingLanguage = false,
  hasLanguageError = false,
  isRecordingRestricted = false,
  isLoadingPatient = false,
  isCheckingPausedConsultation = false,
  resume,
  onLanguageChange,
  onStartRecording,
  onStartWithoutRecording,
  onBackToOverview,
  children,
}: PatientOverviewFooterProps) => {
  const { t } = useTranslation();
  const isNoRecordingSelected =
    isRecordingRestricted || selectedLanguageCode === NO_RECORDING_SELECTION;
  const isStartDisabled =
    isLoadingPatient ||
    (!isNoRecordingSelected &&
      (isCheckingPausedConsultation || isResolvingLanguage || hasLanguageError));
  const languageNameOf = (code: string) =>
    t(`languages.${code}`, {
      defaultValue:
        languagesData?.languages.find((language) => language.code === code)?.name ?? code,
    });

  return (
    <FooterContainer>
      {children}
      <FooterButtonRow>
        <BackButton onClick={onBackToOverview}>
          <ChevronLeft className="size-4 shrink-0" aria-hidden />
          {t('patientOverview.backToOverview')}
        </BackButton>
        {resume ? (
          <ResumeRecordingButton data-ph-no-autocapture onClick={resume.onResume}>
            <Play className="size-4 shrink-0" aria-hidden />
            {resume.languageCode
              ? t('patientOverview.footer.resumeRecording', {
                  language: languageNameOf(resume.languageCode),
                })
              : t('recording.resume')}
            <ArrowRight className="size-4 shrink-0" aria-hidden />
          </ResumeRecordingButton>
        ) : (
          <RecordingActionGroup>
            <RecordingActionButton
              data-ph-no-autocapture
              disabled={isStartDisabled}
              $roundedFull={isRecordingRestricted}
              onClick={isNoRecordingSelected ? onStartWithoutRecording : onStartRecording}
            >
              {isNoRecordingSelected ? (
                <MicOff className="size-4 shrink-0" aria-hidden />
              ) : (
                <Mic className="size-4 shrink-0" aria-hidden />
              )}
              <RecordingActionLabel>
                <RecordingActionTitle>
                  {isNoRecordingSelected
                    ? t('patientOverview.footer.noRecordingOption')
                    : t('recording.start')}
                </RecordingActionTitle>
                {!isNoRecordingSelected && (
                  <RecordingActionSubtitle>
                    {hasLanguageError
                      ? t('patientOverview.footer.languageError')
                      : isResolvingLanguage
                        ? t('patientOverview.footer.resolvingLanguage')
                        : t('patientOverview.footer.selectedLanguage', {
                            language: languageNameOf(selectedLanguageCode),
                          })}
                  </RecordingActionSubtitle>
                )}
              </RecordingActionLabel>
            </RecordingActionButton>
            {!isRecordingRestricted && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <LanguageMenuTrigger data-ph-no-autocapture aria-label={t('recording.language')}>
                    <ChevronDown className="size-4 shrink-0" aria-hidden />
                  </LanguageMenuTrigger>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-72">
                  <LanguageMenuHeader>
                    <LanguageMenuTitle>
                      {t('patientOverview.footer.languageMenu.title')}
                    </LanguageMenuTitle>
                    <LanguageMenuDescription>
                      {t('patientOverview.footer.languageMenu.description')}
                    </LanguageMenuDescription>
                  </LanguageMenuHeader>
                  {(languagesData?.languages ?? []).map((language) => {
                    const isSelected = language.code === selectedLanguageCode;
                    const isLastUsed = language.code === lastUsedLanguageCode;
                    return (
                      <LanguageOption
                        key={language.code}
                        $selected={isSelected}
                        onClick={() => onLanguageChange(language.code)}
                      >
                        <LanguageOptionCode $selected={isSelected}>
                          {formatLanguageCode(language.code)}
                        </LanguageOptionCode>
                        <LanguageOptionLabel>
                          {t(`languages.${language.code}`, { defaultValue: language.name })}
                        </LanguageOptionLabel>
                        {isLastUsed && (
                          <LanguageOptionMeta>
                            {t('patientOverview.footer.languageMenu.recentlyUsed')}
                          </LanguageOptionMeta>
                        )}
                        {isSelected && (
                          <Check className="size-4 shrink-0 text-primary" aria-hidden />
                        )}
                      </LanguageOption>
                    );
                  })}
                  <DropdownMenuSeparator />
                  <NoRecordingOption onClick={() => onLanguageChange(NO_RECORDING_SELECTION)}>
                    <MicOff className="size-4 shrink-0 text-foreground" aria-hidden />
                    {t('patientOverview.footer.noRecordingOption')}
                  </NoRecordingOption>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </RecordingActionGroup>
        )}
      </FooterButtonRow>
    </FooterContainer>
  );
};
